import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PHOTO_ESTIMATE_PROVIDERS, PHOTO_ESTIMATE_REASONS } from '@kraftwerk/shared';
import type { DaySlot, PhotoEstimateProvider } from '@kraftwerk/shared';
import { apiFetch } from '../../api.js';
import { resizeToJpeg } from '../../db/photos.js';
import type { PreparedPhoto } from '../../db/photos.js';
import {
  acceptEstimate,
  discardEstimate,
  resendEstimate,
  startEstimate,
} from '../../db/estimates.js';
import { db } from '../../db/local.js';
import type { LocalEstimateJob } from '../../db/local.js';
import { toNumber } from './FoodForm.js';

/**
 * Gibt es die Schätzung per Foto für dieses Konto — und wer schätzt? Fragt den
 * Server. Ohne Anbieter, offline, für Kinder (ein Teen nur mit
 * Ernährungsfreigabe — das entscheidet der Server): `null`, und der Knopf
 * fehlt. Ein unbekannter Anbieter zählt als „nein“: Ohne passenden Hinweis
 * wird nichts gesendet.
 */
export function usePhotoEstimateProvider(): PhotoEstimateProvider | null {
  const [provider, setProvider] = useState<PhotoEstimateProvider | null>(null);
  useEffect(() => {
    let live = true;
    apiFetch<{ available: boolean; provider?: string }>('/nutrition/photo-estimate')
      .then((body) => {
        const known = (PHOTO_ESTIMATE_PROVIDERS as readonly string[]).includes(body.provider ?? '');
        if (live)
          setProvider(
            body.available === true && known ? (body.provider as PhotoEstimateProvider) : null,
          );
      })
      .catch(() => {
        if (live) setProvider(null);
      });
    return () => {
      live = false;
    };
  }, []);
  return provider;
}

/**
 * Kalorien aus einem Foto schätzen — seit dem 28.10.2026 **im Hintergrund**.
 *
 * Foto wählen (auf dem Gerät verkleinert, ohne EXIF) → **ausdrücklich
 * bestätigen**, dass genau dieses Foto geschätzt wird — der Hinweis sagt je
 * Anbieter ehrlich, wohin es geht → „Schätzen lassen“. Danach steht sofort ein
 * Platzhalter in der Mahlzeit (`EstimatePlaceholder`), und man kann gehen.
 */
export function PhotoEstimateButton({
  date,
  slot,
  language,
  onStarted,
}: {
  date: string;
  slot: DaySlot;
  language: string;
  onStarted: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const provider = usePhotoEstimateProvider();
  const inputId = useId();
  const headingId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<PreparedPhoto | null>(null);
  const [state, setState] = useState<'idle' | 'preparing' | 'busy'>('idle');
  const [problem, setProblem] = useState<'photo' | null>(null);

  if (!provider) return null;

  const pick = (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    setState('preparing');
    resizeToJpeg(file)
      .then(setPhoto)
      .catch(() => setProblem('photo'))
      .finally(() => setState('idle'));
  };

  const send = () => {
    if (!photo) return;
    setState('busy');
    void startEstimate(
      { date, slot, photo: photo.blob, language: language.startsWith('en') ? 'en' : 'de' },
      db,
    ).then(() => {
      setPhoto(null);
      setState('idle');
      onStarted();
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {photo ? (
        <div
          role="group"
          aria-labelledby={headingId}
          className="flex flex-col gap-2 rounded-xl border border-warn p-3 text-sm"
        >
          <h4 id={headingId} className="font-semibold">
            {t(`nutrition.day.photo.consentHeading.${provider}`)}
          </h4>
          <p>{t(`nutrition.day.photo.consent.${provider}`)}</p>
          <p className="text-xs text-ink-muted">{t('nutrition.day.photo.consentResult')}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={send}
              disabled={state === 'busy'}
              className="kw-btn kw-btn-small flex-1"
            >
              {state === 'busy' ? t('nutrition.day.photo.busy') : t('nutrition.day.photo.send')}
            </button>
            <button
              type="button"
              onClick={() => {
                setPhoto(null);
                setProblem(null);
              }}
              disabled={state === 'busy'}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('nutrition.day.photo.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <label htmlFor={inputId} className="sr-only">
            {t('nutrition.day.photo.pick')}
          </label>
          <input
            ref={input}
            id={inputId}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            onChange={(event) => {
              pick(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={state !== 'idle'}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {state === 'preparing'
              ? t('nutrition.day.photo.preparing')
              : t('nutrition.day.photo.start')}
          </button>
        </>
      )}
      {problem ? (
        <p role="alert" className="text-xs text-warn">
          {t(`nutrition.day.photo.error.${problem}`)}
        </p>
      ) : null}
    </div>
  );
}

/** Gründe mit eigenem Text; alles andere heißt „fehlgeschlagen“. */
const KNOWN_REASONS: readonly string[] = [...PHOTO_ESTIMATE_REASONS, 'offline', 'tooLarge'];
/** Hier hilft erneutes Senden nicht. */
const FINAL_REASONS: readonly string[] = ['disabled', 'child', 'tooLarge'];

/**
 * Der Platzhalter einer Foto-Schätzung in ihrer Mahlzeit (28.10.2026).
 *
 * Er zählt in keiner Summe — die Kachel sagt „Schätzung offen“, der Tag
 * „enthält eine offene Schätzung“. Ist der Vorschlag da: Gericht, Werte,
 * angenommene Portion, Sicherheit — „Passt“, „Anpassen“, „Verwerfen“. Erst
 * dann wird eingetragen. Gescheitert: Grund und „Erneut senden“ (das Foto
 * liegt dafür noch auf diesem Gerät).
 */
export function EstimatePlaceholder({ job }: { job: LocalEstimateJob }): React.JSX.Element {
  const { t } = useTranslation();
  const headingId = useId();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const result = job.result;

  const discardButton = (
    <button
      type="button"
      onClick={() => {
        setBusy(true);
        void discardEstimate(job.id, db).finally(() => setBusy(false));
      }}
      disabled={busy}
      className="kw-btn kw-btn-quiet kw-btn-small flex-1"
    >
      {t('nutrition.day.photo.pending.discard')}
    </button>
  );

  if (job.status === 'done' && result) {
    const note = result.portion ? t('nutrition.day.photo.note', { portion: result.portion }) : null;
    return (
      <li
        aria-labelledby={headingId}
        className="flex flex-col gap-2 rounded-xl border border-warn px-3 py-2 text-sm"
      >
        <span id={headingId} className="font-semibold">
          {t('nutrition.day.photo.resultHeading')}
        </span>
        <span className="break-words">
          {t('nutrition.day.photo.pending.dish', { dish: result.dish })}
        </span>
        <span className="text-xs">
          {t('nutrition.day.photo.pending.values', {
            kcal: Math.round(result.kcal),
            protein: Math.round(result.protein),
            carbs: Math.round(result.carbs),
            fat: Math.round(result.fat),
          })}
        </span>
        {result.portion ? (
          <span className="text-xs">
            {t('nutrition.day.photo.portion', { portion: result.portion })}
          </span>
        ) : null}
        <span className="text-xs">{t(`nutrition.day.photo.confidence.${result.confidence}`)}</span>
        <span className="text-xs text-warn">{t('nutrition.day.photo.resultHint')}</span>
        {editing ? (
          <AdjustEstimate job={job} note={note} onCancel={() => setEditing(false)} />
        ) : (
          <span className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void acceptEstimate(job.id, { ...result, label: result.dish }, note, db).finally(
                  () => setBusy(false),
                );
              }}
              className="kw-btn kw-btn-small flex-1"
            >
              {t('nutrition.day.photo.pending.accept')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(true)}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('nutrition.day.photo.pending.adjust')}
            </button>
            {discardButton}
          </span>
        )}
      </li>
    );
  }

  if (job.status === 'failed') {
    const reason = job.reason && KNOWN_REASONS.includes(job.reason) ? job.reason : 'unknown';
    const canResend = job.photo !== null && !FINAL_REASONS.includes(reason);
    return (
      <li
        aria-labelledby={headingId}
        className="flex flex-col gap-2 rounded-xl border border-warn px-3 py-2 text-sm"
      >
        <span id={headingId} className="font-semibold">
          {t('nutrition.day.photo.pending.failedHeading')}
        </span>
        <span role="alert" className="text-xs text-warn">
          {t(`nutrition.day.photo.error.${reason}`)}
        </span>
        {job.photo === null ? (
          <span className="text-xs text-ink-muted">{t('nutrition.day.photo.pending.noPhoto')}</span>
        ) : null}
        <span className="flex flex-wrap gap-2">
          {canResend ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void resendEstimate(job.id, db).finally(() => setBusy(false));
              }}
              className="kw-btn kw-btn-small flex-1"
            >
              {t('nutrition.day.photo.pending.resend')}
            </button>
          ) : null}
          {discardButton}
        </span>
      </li>
    );
  }

  // Unterwegs, wartend, läuft — oder fertig, aber noch verschlüsselt.
  const locked = job.status === 'done';
  return (
    <li
      aria-labelledby={headingId}
      aria-busy={!locked}
      className="flex flex-col gap-1 rounded-xl border border-dashed border-warn px-3 py-2 text-sm"
    >
      <span id={headingId} className="font-semibold">
        {locked ? t('nutrition.day.photo.resultHeading') : t('nutrition.day.photo.pending.heading')}
      </span>
      <span role="status" className="text-xs text-ink-muted">
        {locked
          ? t('nutrition.day.photo.pending.locked')
          : job.status === 'unsent'
            ? t('nutrition.day.photo.pending.unsent')
            : job.status === 'running' || job.position === 0
              ? t('nutrition.day.photo.pending.running')
              : job.position !== null
                ? t('nutrition.day.photo.pending.queued', { position: job.position + 1 })
                : t('nutrition.day.photo.pending.hint')}
      </span>
      {locked ? null : (
        <span className="text-xs text-ink-muted">{t('nutrition.day.photo.pending.hint')}</span>
      )}
      <span className="mt-1 flex">{discardButton}</span>
    </li>
  );
}

/** „Anpassen“: Werte vor dem Eintragen ändern — „geschätzt“ bleibt trotzdem stehen. */
function AdjustEstimate({
  job,
  note,
  onCancel,
}: {
  job: LocalEstimateJob;
  note: string | null;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const result = job.result!;
  const labelId = useId();
  const [label, setLabel] = useState(result.dish);
  const [values, setValues] = useState({
    kcal: String(Math.round(result.kcal)),
    protein: String(Math.round(result.protein)),
    carbs: String(Math.round(result.carbs)),
    fat: String(Math.round(result.fat)),
  });
  const fields = ['kcal', 'protein', 'carbs', 'fat'] as const;

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const kcal = toNumber(values.kcal);
        if (label.trim() === '' || kcal === null) return;
        void acceptEstimate(
          job.id,
          {
            label,
            kcal,
            protein: toNumber(values.protein) ?? 0,
            carbs: toNumber(values.carbs) ?? 0,
            fat: toNumber(values.fat) ?? 0,
          },
          note,
          db,
        );
      }}
    >
      <label htmlFor={labelId} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('nutrition.day.freeLabel')}
        <input
          id={labelId}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={160}
          className="kw-field"
        />
      </label>
      <div className="flex flex-wrap gap-2">
        {fields.map((field) => (
          <AdjustField
            key={field}
            label={t(`nutrition.macro.${field}`)}
            value={values[field]}
            onChange={(value) => setValues((current) => ({ ...current, [field]: value }))}
          />
        ))}
      </div>
      <span className="flex flex-wrap gap-2">
        <button type="submit" className="kw-btn kw-btn-small flex-1">
          {t('nutrition.day.photo.pending.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('nutrition.library.cancel')}
        </button>
      </span>
    </form>
  );
}

function AdjustField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <label htmlFor={id} className="kw-label flex w-20 flex-col gap-1.5">
      {label}
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="kw-field"
      />
    </label>
  );
}
